import axios from "axios";
import { API_BASE_URL } from "../../config";

/**
 * The send log — every proposal and quotation that went out, who sent it, how,
 * and what happened to it since.
 */

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("cms_token")}`,
  "Content-Type": "application/json",
});

const apiError = (err, fallback) => {
  const data = err?.response?.data;
  const firstFieldError = data?.errors?.[Object.keys(data?.errors || {})[0]]?.[0];
  return new Error(firstFieldError || data?.message || data?.error || fallback);
};

/**
 * @returns {{rows: Array, counts: object, pagination: object}}
 *          `counts` follows the filters, so the cards and the table always agree.
 */
export const fetchSends = async (params = {}) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/proposals/sends`, {
      headers: authHeaders(),
      params,
    });
    const body = res.data || {};
    return {
      rows: Array.isArray(body.data) ? body.data : [],
      counts: body.counts || { proposal: 0, quotation: 0, total: 0 },
      pagination: body.pagination || { page: 1, limit: 20, total: 0, total_pages: 1 },
    };
  } catch (err) {
    throw apiError(err, "Failed to load the send log");
  }
};

/**
 * Send a document and record that it went.
 *
 * On `channel: "email"` the backend mails the `html` we hand it — the same
 * markup the rep previewed — so the client cannot receive a second, different
 * rendering. Every other channel only writes the row.
 *
 * `payload` carries the form inputs so the document can be rebuilt from the log.
 */
export const recordSend = async (payload) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/proposals/sends`, payload, {
      headers: authHeaders(),
    });
    const body = res.data || {};
    if (body.status === 422 || body.status === false) {
      throw new Error(body.message || "This send could not be recorded");
    }
    /* The API says what it actually did — "Email sent to client successfully."
       when it mailed the PDF, something else when it only logged the row. */
    return { data: body.data ?? null, message: body.message || "" };
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "This send could not be recorded");
  }
};

/** viewed / accepted / rejected / expired — whatever the recipient did next. */
export const updateSendStatus = async (id, status) => {
  try {
    const res = await axios.post(
      `${API_BASE_URL}/proposals/sends/${id}/status`,
      { status },
      { headers: authHeaders() },
    );
    return { data: res.data?.data ?? null, message: res.data?.message || "" };
  } catch (err) {
    throw apiError(err, "The status could not be updated");
  }
};

/**
 * One send, with the inputs it was built from.
 *
 * The list does not carry `payload` — it would be dead weight on every row — so
 * reopening a document, or quoting the same client a second time, fetches it
 * here first.
 */
export const fetchSend = async (id) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/proposals/sends/${id}`, {
      headers: authHeaders(),
    });
    return res.data?.data || null;
  } catch (err) {
    throw apiError(err, "That send could not be opened");
  }
};
